import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Prohibit, ArrowCounterClockwise, MagnifyingGlass, XCircle, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API, formatErr } from "@/lib/api";
import { StatusBadge } from "@/pages/Overview";

function Tabs({ value, onChange, items }) {
  return (
    <div className="inline-flex flex-wrap gap-1 rounded-md border border-stone-200 bg-white p-1">
      {items.map(([key, label, n]) => (
        <button key={key} onClick={() => onChange(key)} className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${value === key ? "bg-emerald-900 text-white" : "text-stone-700 hover:bg-stone-100"}`} data-testid={`admin-tab-${key}`}>
          {label}{n !== undefined ? ` (${n})` : ""}
        </button>
      ))}
    </div>
  );
}

export function AdminUsers() {
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const load = async () => {
    try { const { data } = await api.get("/admin/users"); setRows(data); } catch {}
  };
  useEffect(() => { load(); }, []);

  const act = async (path, ok) => {
    try { await api.post(path); toast.success(ok); load(); }
    catch (ex) { toast.error(formatErr(ex?.response?.data?.detail)); }
  };
  const verify = (id) => act(`/admin/verify/${id}`, "User verified");
  const suspend = (u) => window.confirm(`Suspend ${u.name}? They are logged out at once and cannot log in.`) && act(`/admin/users/${u.id}/suspend`, "User suspended");
  const restore = (id) => act(`/admin/users/${id}/unsuspend`, "User restored");

  const counts = useMemo(() => ({
    all: rows?.length ?? 0,
    pending: rows?.filter((u) => !u.verified && u.role !== "admin").length ?? 0,
    suspended: rows?.filter((u) => u.active === false).length ?? 0,
  }), [rows]);
  const shown = (rows || []).filter((u) => {
    if (tab === "pending" && (u.verified || u.role === "admin")) return false;
    if (tab === "suspended" && u.active !== false) return false;
    const needle = q.trim().toLowerCase();
    return !needle || `${u.name} ${u.email} ${u.state || ""} ${u.district || ""}`.toLowerCase().includes(needle);
  });

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-stone-500">Admin</div>
          <div className="font-display text-2xl font-bold text-stone-900">All Users</div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Tabs value={tab} onChange={setTab} items={[["all", "All", counts.all], ["pending", "KYC pending", counts.pending], ["suspended", "Suspended", counts.suspended]]} />
          <label className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, place" className="pl-8 pr-3 py-2 rounded-md border border-stone-300 text-sm w-64" data-testid="admin-user-search" />
          </label>
        </div>
      </div>
      <div className="rounded-xl bg-white border border-stone-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-stone-50 text-stone-500 text-xs uppercase tracking-widest font-bold">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Role</th>
              <th className="text-left p-3">Location</th>
              <th className="text-center p-3">Rating</th>
              <th className="text-center p-3">KYC</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows === null && Array.from({ length: 5 }).map((_, i) => (
              <tr key={i}><td colSpan={7} className="p-3"><div className="h-6 shimmer rounded" /></td></tr>
            ))}
            {rows && shown.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-stone-500">No users here.</td></tr>}
            {shown.map((u, i) => (
              <motion.tr key={u.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }} className={`border-t border-stone-100 hover:bg-stone-50 ${u.active === false ? "opacity-60" : ""}`} data-testid={`admin-user-row-${u.id}`}>
                <td className="p-3 font-semibold">{u.name}{u.active === false && <span className="ml-2 text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-red-100 text-red-800">Suspended</span>}</td>
                <td className="p-3 text-stone-600">{u.email}</td>
                <td className="p-3 capitalize font-semibold">{u.role}</td>
                <td className="p-3 text-xs text-stone-500">{u.district || "—"}, {u.state || "—"}</td>
                <td className="p-3 text-center font-mono">{u.rating_count ? u.rating_avg?.toFixed?.(1) : "—"}</td>
                <td className="p-3 text-center">
                  <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${u.verified ? "bg-emerald-100 text-emerald-800" : "bg-orange-100 text-orange-800"}`}>{u.kyc_status || "pending"}</span>
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  {!u.verified && u.role !== "admin" && (
                    <button onClick={() => verify(u.id)} className="inline-flex items-center gap-1 text-xs font-bold text-white bg-emerald-800 hover:bg-emerald-700 px-3 py-1.5 rounded mr-2" data-testid={`verify-user-${u.id}`}>
                      <CheckCircle size={12} weight="bold" /> Verify
                    </button>
                  )}
                  {u.role !== "admin" && (u.active === false ? (
                    <button onClick={() => restore(u.id)} className="inline-flex items-center gap-1 text-xs font-bold text-stone-700 border border-stone-300 hover:bg-stone-100 px-3 py-1.5 rounded" data-testid={`restore-user-${u.id}`}>
                      <ArrowCounterClockwise size={12} weight="bold" /> Restore
                    </button>
                  ) : (
                    <button onClick={() => suspend(u)} className="inline-flex items-center gap-1 text-xs font-bold text-red-700 border border-red-300 hover:bg-red-50 px-3 py-1.5 rounded" data-testid={`suspend-user-${u.id}`}>
                      <Prohibit size={12} weight="bold" /> Suspend
                    </button>
                  ))}
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// what the admin has to do next on a deal
const NEEDS = { matched: "Confirm payment received", disputed: "Settle the dispute" };

export function AdminDeals() {
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState("todo");
  useEffect(() => { (async () => { try { const { data } = await api.get("/admin/deals"); setRows(data); } catch {} })(); }, []);

  const todo = (rows || []).filter((d) => NEEDS[d.status]);
  const shown = tab === "todo" ? todo : rows || [];

  return (
    <div className="space-y-6" data-testid="admin-deals-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-stone-500">Admin</div>
          <div className="font-display text-2xl font-bold text-stone-900">All Deals · Platform Wide</div>
          <div className="text-xs text-stone-500 mt-1">Open a deal to confirm a payment you have received, or to settle a dispute.</div>
        </div>
        <Tabs value={tab} onChange={setTab} items={[["todo", "Needs my action", rows ? todo.length : undefined], ["all", "All deals", rows ? rows.length : undefined]]} />
      </div>
      <div className="rounded-xl bg-white border border-stone-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-stone-50 text-stone-500 text-xs uppercase tracking-widest font-bold">
            <tr>
              <th className="text-left p-3">Deal</th>
              <th className="text-left p-3">Commodity</th>
              <th className="text-left p-3">Buyer</th>
              <th className="text-left p-3">Seller</th>
              <th className="text-right p-3">Value</th>
              <th className="text-center p-3">Status</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows === null && Array.from({ length: 4 }).map((_, i) => (
              <tr key={i}><td colSpan={7} className="p-3"><div className="h-6 shimmer rounded" /></td></tr>
            ))}
            {rows && shown.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-stone-500">{tab === "todo" ? "Nothing waiting for you." : "No deals yet."}</td></tr>}
            {shown.map((d, i) => (
              <motion.tr key={d.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }} className="border-t border-stone-100 hover:bg-stone-50">
                <td className="p-3 font-mono text-xs"><Link to={`/app/deals/${d.id}`} className="text-emerald-800 hover:underline">#{d.id.slice(-8)}</Link></td>
                <td className="p-3 font-semibold">{d.commodity} · {d.quantity}</td>
                <td className="p-3 text-stone-600">{d.buyer_name}</td>
                <td className="p-3 text-stone-600">{d.seller_name}</td>
                <td className="p-3 text-right font-mono font-semibold">₹{(d.total_value || 0).toLocaleString("en-IN")}</td>
                <td className="p-3 text-center"><StatusBadge status={d.status} /></td>
                <td className="p-3 text-right">
                  <Link to={`/app/deals/${d.id}`} className={`text-xs font-bold px-3 py-1.5 rounded ${NEEDS[d.status] ? "bg-orange-600 text-white hover:bg-orange-700" : "border border-stone-300 text-stone-700 hover:bg-stone-100"}`} data-testid={`admin-open-deal-${d.id}`}>
                    {NEEDS[d.status] || "Open"}
                  </Link>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const KYC_TYPE = { id_proof: "ID proof", gstin: "GST registration", fssai: "FSSAI", mandi_licence: "Mandi / trader licence", bank_proof: "Bank proof", other: "Other" };

export function AdminKyc() {
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState("pending");
  const load = async (status = tab) => {
    setRows(null);
    try { const { data } = await api.get("/admin/kyc", { params: { status } }); setRows(data); } catch { setRows([]); }
  };
  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); load(); } catch (ex) { toast.error(formatErr(ex?.response?.data?.detail)); }
  };
  const approve = (d) => act(() => api.post(`/admin/kyc/${d.id}/approve`), "Approved");
  const reject = (d) => {
    const reason = window.prompt("Why is it rejected? The user will read this.");
    if (reason && reason.trim().length >= 3) act(() => api.post(`/admin/kyc/${d.id}/reject`, { reason }), "Rejected");
  };
  const verifyUser = (d) => act(() => api.post(`/admin/verify/${d.user.id}`), "User verified");

  return (
    <div className="space-y-6" data-testid="admin-kyc-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-stone-500">Admin</div>
          <div className="font-display text-2xl font-bold text-stone-900">KYC papers</div>
          <div className="text-xs text-stone-500 mt-1">Open the paper, approve or reject it, and mark the user verified when you are satisfied.</div>
        </div>
        <Tabs value={tab} onChange={setTab} items={[["pending", "Waiting"], ["approved", "Approved"], ["rejected", "Rejected"]]} />
      </div>
      <div className="rounded-xl bg-white border border-stone-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-stone-50 text-stone-500 text-xs uppercase tracking-widest font-bold">
            <tr>
              <th className="text-left p-3">User</th>
              <th className="text-left p-3">Paper</th>
              <th className="text-left p-3">Number</th>
              <th className="text-left p-3">Uploaded</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={5} className="p-3"><div className="h-6 shimmer rounded" /></td></tr>}
            {rows && rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-stone-500">Nothing here.</td></tr>}
            {rows?.map((d) => (
              <tr key={d.id} className="border-t border-stone-100 hover:bg-stone-50" data-testid={`admin-kyc-row-${d.id}`}>
                <td className="p-3">
                  <div className="font-semibold">{d.user?.name || "—"}</div>
                  <div className="text-xs text-stone-500">{d.user?.email} · <span className="capitalize">{d.user?.role}</span>{d.user?.state ? ` · ${d.user.state}` : ""}</div>
                </td>
                <td className="p-3">{KYC_TYPE[d.doc_type] || d.doc_type}</td>
                <td className="p-3 font-mono text-xs">{d.doc_number || "—"}</td>
                <td className="p-3 text-xs text-stone-500">{new Date(d.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <a href={`${API}/kyc/documents/${d.id}/file`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-stone-700 border border-stone-300 hover:bg-stone-100 px-3 py-1.5 rounded mr-2"><Eye size={12} weight="bold" /> Open</a>
                  {d.status === "pending" && (
                    <>
                      <button onClick={() => approve(d)} className="inline-flex items-center gap-1 text-xs font-bold text-white bg-emerald-800 hover:bg-emerald-700 px-3 py-1.5 rounded mr-2" data-testid={`kyc-approve-${d.id}`}><CheckCircle size={12} weight="bold" /> Approve</button>
                      <button onClick={() => reject(d)} className="inline-flex items-center gap-1 text-xs font-bold text-red-700 border border-red-300 hover:bg-red-50 px-3 py-1.5 rounded mr-2" data-testid={`kyc-reject-${d.id}`}><XCircle size={12} weight="bold" /> Reject</button>
                    </>
                  )}
                  {d.status === "approved" && d.user && !d.user.verified && (
                    <button onClick={() => verifyUser(d)} className="text-xs font-bold text-white bg-orange-600 hover:bg-orange-700 px-3 py-1.5 rounded" data-testid={`kyc-verify-user-${d.id}`}>Mark user verified</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
